// src/components/SymbolSearchDialog.jsx
//
// Reads symbols from SymbolContext (already loaded on app start).
// All filtering happens locally — instant, no API calls on keystroke.

import React, { useState, useEffect, useRef } from "react";
import { useSymbols } from "../context/SymbolContext";
// const LOGO_BASE = process.env.REACT_APP_IMAGE_URL || "http://localhost/images/symbols";
const LOGO_BASE = "https://api.orbitsecurities.com.pk/images/symbols/";

// ─── Local filter logic ───────────────────────────────────────────────────────
function filterSymbols(symbols, query, sectorCode) {
    const q = query.trim().toLowerCase();
    return symbols.filter((s) => {
        const matchSector =
            !sectorCode ||
            (s.sector_name || s.sector || "").toLowerCase().includes(sectorCode.toLowerCase());

        const matchQuery =
            !q ||
            (s.code || "").toLowerCase().includes(q) ||
            (s.company_name || s.name || "").toLowerCase().includes(q) ||
            (s.sector_name || s.sector || "").toLowerCase().includes(q);

        return matchSector && matchQuery;
    });
}

function groupBySector(items) {
    return items.reduce((acc, item) => {
        const key = item.sector_name || item.sector || "General";
        (acc[key] = acc[key] || []).push(item);
        return acc;
    }, {});
}

// ─── Styles (injected once) ───────────────────────────────────────────────────
const STYLE_ID = "ssd-styles";
const CSS = `
.ssd-backdrop {
  position: fixed; inset: 0;
  background: rgba(5, 10, 48, 0.70);
  display: flex; align-items: flex-start; justify-content: center;
  z-index: 99999; padding-top: 80px;
  animation: ssdFadeIn 0.15s ease;
}
.ssd-dialog {
  background: #fff; border-radius: 16px;
  width: calc(100% - 32px); max-width: 580px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(5,10,48,0.28);
  max-height: 75vh; display: flex; flex-direction: column;
  animation: ssdSlideUp 0.18s ease;
}
.ssd-header { padding: 16px 20px 0; }
.ssd-search-wrap {
  display: flex; align-items: center; gap: 10px;
  border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: 10px 14px; transition: border-color 0.2s;
}
.ssd-search-wrap:focus-within { border-color: #f98300; }
.ssd-search-icon { color: #aaa; display: flex; align-items: center; }
.ssd-input {
  flex: 1; border: none; outline: none;
  font-size: 15px; color: #050a30;
  background: transparent; font-family: inherit;
}
.ssd-input::placeholder { color: #bbb; }
.ssd-count { font-size: 11px; color: #ccc; white-space: nowrap; }
.ssd-esc {
  background: #f5f5f5; border: 1px solid #ddd;
  border-radius: 4px; padding: 2px 8px;
  font-size: 11px; color: #999; cursor: pointer;
  font-family: monospace;
}
.ssd-filters { display: flex; gap: 8px; padding: 10px 20px 0; flex-wrap: wrap; }
.ssd-filter {
  padding: 4px 12px; border-radius: 20px;
  border: 1px solid #e5e7eb; background: #fff;
  font-size: 12px; color: #666; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.ssd-filter:hover { border-color: #f98300; color: #f98300; }
.ssd-filter.active { background: #fff7ed; border-color: #f98300; color: #f98300; font-weight: 600; }
.ssd-divider { height: 1px; background: #f0f0f0; margin: 10px 0 0; }
.ssd-results { overflow-y: auto; flex: 1; }
.ssd-group-label {
  padding: 10px 20px 4px; font-size: 10px; font-weight: 600;
  color: #bbb; letter-spacing: 0.08em; text-transform: uppercase;
}
.ssd-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  width: 100%;              /* ✅ lock width */
  box-sizing: border-box;   /* ✅ prevent overflow shift */
}
.ssd-row:hover { background: #fff7ed; }
.ssd-badge {
  background: #fff7ed; color: #f98300; font-weight: 700;
  font-size: 12px; padding: 5px 10px; border-radius: 6px;
  min-width: 56px; text-align: center;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.05em; flex-shrink: 0;
}
.ssd-logo {
  width: 56px;              /* bigger */
  height: 56px;             /* PERFECT square */
  object-fit: contain;      /* keep logo ratio */
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;      /* slight modern rounding */
  padding: 6px;
  flex-shrink: 0;
}
.ssd-meta { flex: 1; min-width: 0; }
.ssd-sector { font-size: 11px; color: #aaa; margin-top: 2px; }
.ssd-arrow { color: #ddd; font-size: 16px; flex-shrink: 0; transition: color 0.15s; }
.ssd-row:hover .ssd-arrow { color: #f98300; }
.ssd-empty { padding: 48px 20px; text-align: center; color: #bbb; font-size: 14px; }
.ssd-loading { padding: 32px 20px; text-align: center; color: #bbb; font-size: 14px; }
.ssd-error { padding: 24px 20px; text-align: center; color: #e24b4a; font-size: 13px; }
.ssd-footer {
  padding: 10px 20px; border-top: 1px solid #f0f0f0;
  display: flex; gap: 20px; align-items: center;
}
.ssd-hint { font-size: 11px; color: #ccc; display: flex; align-items: center; gap: 4px; }
.ssd-hint-kbd {
  background: #f5f5f5; border: 1px solid #e0e0e0;
  border-radius: 3px; padding: 1px 5px;
  font-family: monospace; font-size: 10px; color: #999;
}
  .ssd-name {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #050a30;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ssd-code-inline {
  font-weight: 700;
  color: #f98300;
  letter-spacing: 0.05em;
}

.ssd-dash {
  color: #999;
}

.ssd-title-inline {
  color: #050a30;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ssd-sector {
  font-size: 11px;
  color: #aaa;
  margin-top: 2px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
@keyframes ssdFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes ssdSlideUp {
  from { opacity: 0; transform: translateY(-12px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

function injectStyles() {
    if (!document.getElementById(STYLE_ID)) {
        const tag = document.createElement("style");
        tag.id = STYLE_ID;
        tag.textContent = CSS;
        document.head.appendChild(tag);
    }
}

// ─── Default filter pills ─────────────────────────────────────────────────────
const DEFAULT_FILTERS = [
    { label: "All", sectorCode: "" },
    { label: "Banking", sectorCode: "bank" },
    { label: "Oil & Gas", sectorCode: "oil" },
    { label: "Cement", sectorCode: "cement" },
    { label: "Fertilizer", sectorCode: "fertilizer" },
    { label: "Tech", sectorCode: "tech" },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function SymbolSearchDialog({
    open,
    onClose,
    onSelect,
    filters = DEFAULT_FILTERS,
    placeholder = "Search symbol or company name…",
}) {
    const { symbols, loading: symbolsLoading, error: symbolsError } = useSymbols();
    const [query, setQuery] = useState("");
    const [activeFilter, setActiveFilter] = useState(filters[0]);
    const inputRef = useRef(null);

    useEffect(() => { injectStyles(); }, []);

    useEffect(() => {
        if (open) {
            setQuery("");
            setActiveFilter(filters[0]);
            setTimeout(() => inputRef.current?.focus(), 60);
        }
    }, [open]);

    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") handleClose(); };
        if (open) window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open]);

    const handleClose = () => { setQuery(""); onClose?.(); };

    if (!open) return null;

    const filtered = filterSymbols(symbols, query, activeFilter.sectorCode);
    const grouped = groupBySector(filtered);

    return (
        <div
            className="ssd-backdrop"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="ssd-dialog">
                <div className="ssd-header">
                    <div className="ssd-search-wrap">
                        <span className="ssd-search-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.2"
                                strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </span>
                        <input
                            ref={inputRef}
                            className="ssd-input"
                            placeholder={placeholder}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        {query && <span className="ssd-count">{filtered.length} results</span>}
                        <span className="ssd-esc" onClick={handleClose}>ESC</span>
                    </div>
                    <div className="ssd-filters">
                        {filters.map((f) => (
                            <button
                                key={f.label}
                                className={`ssd-filter${activeFilter.label === f.label ? " active" : ""}`}
                                onClick={() => setActiveFilter(f)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="ssd-divider" />
                </div>

                <div className="ssd-results">
                    {symbolsLoading && <div className="ssd-loading">Loading symbols…</div>}
                    {!symbolsLoading && symbolsError && (
                        <div className="ssd-error">
                            {symbolsError}<br />
                            <small>Make sure the API server is running at {process.env.REACT_APP_URL}</small>
                        </div>
                    )}
                    {!symbolsLoading && !symbolsError && filtered.length === 0 && (
                        <div className="ssd-empty">
                            {query ? `No symbols found for "${query}"` : "No symbols available"}
                        </div>
                    )}
                    {!symbolsLoading && !symbolsError &&
                        Object.entries(grouped).map(([sector, items]) => (
                            <div key={sector}>
                                <div className="ssd-group-label">{sector}</div>
                                {items.map((item) => (
                                    <div
                                        key={item.id || item.symbol}
                                        className="ssd-row"
                                        onClick={() => { onSelect?.(item); handleClose(); }}
                                    >
                                        <img
                                            src={`${LOGO_BASE}${item.code}.svg`}
                                            // src={`https://www.tickeranalysts.com/images/logos/logo_ppl.svg`}
                                            alt={item.code}
                                            className="ssd-logo"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.replaceWith(
                                                    Object.assign(document.createElement("span"), {
                                                        className: "ssd-badge",
                                                        textContent: item.code,
                                                    })
                                                );
                                            }}
                                        />

                                        <span className="ssd-badge" style={{ display: "none" }}>
                                            {item.code}
                                        </span>
                                        <div className="ssd-meta">
                                            <div className="ssd-meta">
                                                <div className="ssd-name">
                                                    <span className="ssd-code-inline">{item.code}</span>
                                                    <span className="ssd-dash"> - </span>
                                                    <span className="ssd-title-inline">
                                                        {item.company_name || item.name || ""}
                                                    </span>
                                                </div>

                                                <div className="ssd-sector">
                                                    {item.sector_name || item.sector || ""}
                                                </div>
                                            </div>                                         
                                        </div>
                                        <span className="ssd-arrow">›</span>
                                    </div>
                                ))}
                            </div>
                        ))
                    }
                </div>

                <div className="ssd-footer">
                    <span className="ssd-hint"><span className="ssd-hint-kbd">↵</span> select</span>
                    <span className="ssd-hint"><span className="ssd-hint-kbd">ESC</span> close</span>
                    {!symbolsLoading && (
                        <span className="ssd-hint" style={{ marginLeft: "auto" }}>
                            {symbols.length} symbols loaded
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}